import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, getApiBaseUrl } from '../../utils/apiConfig';
import { getAuthHeaders } from '../../utils/authUtils';
import { serverManagementLogger } from '../../utils/logger';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  BugReport as DebugIcon,
  Report as CriticalIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Assessment as LogIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de, enUS, fr } from 'date-fns/locale';


const LogViewer = () => {
  const { t, i18n } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [statistics, setStatistics] = useState(null);

  // Filter states
  const [levelFilter, setLevelFilter] = useState(['WARN', 'ERROR', 'CRITICAL']);
  const [processFilter, setProcessFilter] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [processes, setProcesses] = useState([]);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteOption, setDeleteOption] = useState('old');
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const levels = ['DEBUG', 'INFO', 'WARN', 'ERROR', 'CRITICAL'];

  // Date locale mapping
  const dateLocales = {
    de: de,
    en: enUS,
    fr: fr
  };

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        skip: page * rowsPerPage,
        limit: rowsPerPage
      });
      
      // OR-Verknüpfung für Level: Alle ausgewählten Levels zusammen
      if (levelFilter.length > 0) {
        params.append('levels', levelFilter.join(','));
      }
      
      // OR-Verknüpfung für Prozesse: Alle ausgewählten Prozesse zusammen
      if (processFilter.length > 0) {
        params.append('processes', processFilter.join(','));
      }
      
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      
      const response = await apiFetch(`/admin/logs?${params}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setLogs(data);
      setTotalCount(data.length);
    } catch (err) {
      setError(err.message);
      serverManagementLogger.error('Failed to load logs', { error: err.message });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, levelFilter, processFilter, startDate, endDate]);

  const loadStatistics = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) params.append('end_date', endDate.toISOString());
      const response = await apiFetch(`/admin/logs/statistics?${params}`);
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (err) {
      serverManagementLogger.error('Failed to load log statistics', { error: err.message });
    }
  }, [startDate, endDate]);

  const loadProcesses = async () => {
    try {
      const response = await apiFetch('/admin/logs?limit=1000');

      if (response.ok) {
        const data = await response.json();
        const uniqueProcesses = [...new Set(data.map(log => log.process))];
        setProcesses(uniqueProcesses.sort());
      }
    } catch (err) {
      serverManagementLogger.error('Failed to load processes', { error: err.message });
    }
  };

  useEffect(() => {
    loadLogs();
    loadStatistics();
    loadProcesses();
  }, [page, rowsPerPage, levelFilter, processFilter, startDate, endDate, loadLogs, loadStatistics]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setLevelFilter(['WARN', 'ERROR', 'CRITICAL']);
    setProcessFilter([]);
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  const handleLevelChange = (event) => {
    const value = event.target.value;
    setLevelFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleProcessChange = (event) => {
    const value = event.target.value;
    setProcessFilter(typeof value === 'string' ? value.split(',') : value);
  };

  // New dialog functions
  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setDeleteOption('old');
    setShowPasswordField(false);
    setPassword('');
    setShowPassword(false);
    setPasswordError('');
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteOption('old');
    setShowPasswordField(false);
    setPassword('');
    setShowPassword(false);
    setPasswordError('');
  };

  const handleDeleteOptionChange = (event) => {
    const value = event.target.value;
    setDeleteOption(value);
    setShowPasswordField(value === 'all');
    setPassword('');
    setPasswordError('');
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setPasswordError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const deleteLogs = async () => {
    try {
      const authHeaders = getAuthHeaders();
      let endpoint = 'http://localhost:25000/admin/logs/cleanup';
      let body = { days_to_keep: 30 };

      if (deleteOption === 'all') {
        if (!password) {
          setPasswordError(t('admin.logViewer.deleteLogsDialog.passwordError'));
          return;
        }
        endpoint = 'http://localhost:25000/admin/logs/delete-all';
        body = { password: password };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await response.json();
        closeDeleteDialog();
        loadLogs();
        loadStatistics();
      } else {
        const errorData = await response.json();
        if (errorData.detail === 'Invalid password') {
          setPasswordError(t('admin.logViewer.deleteLogsDialog.passwordError'));
        } else {
          setError(t('admin.logViewer.deleteLogsDialog.deleteError'));
        }
      }
    } catch (err) {
      setError(t('admin.logViewer.deleteLogsDialog.deleteError'));
      serverManagementLogger.error('Failed to delete logs', { error: err.message });
    }
  };

  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'Process', 'Level', 'Message', 'Additional Data'].join(','),
      ...logs.map(log => [
        log.timestamp,
        log.process,
        log.level,
        `"${log.message.replace(/"/g, '""')}"`,
        log.additional_data ? `"${JSON.stringify(log.additional_data).replace(/"/g, '""')}"` : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    serverManagementLogger.info('Logs exported to CSV', { count: logs.length });
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'DEBUG': return <DebugIcon color="action" />;
      case 'INFO': return <InfoIcon color="info" />;
      case 'WARN': return <WarningIcon color="warning" />;
      case 'ERROR': return <ErrorIcon color="error" />;
      case 'CRITICAL': return <CriticalIcon color="error" />;
      default: return <InfoIcon />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'DEBUG': return 'default';
      case 'INFO': return 'info';
      case 'WARN': return 'warning';
      case 'ERROR': return 'error';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString(i18n.language);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LogIcon color="primary" /> {t('admin.logViewer.title')}
      </Typography>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('admin.logViewer.totalLogs')}
                </Typography>
                <Typography variant="h4">
                  {statistics.total_count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {Object.entries(statistics.level_counts).map(([level, count]) => (
            <Grid item xs={12} md={2} key={level}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {level}
                  </Typography>
                  <Typography variant="h4" color={getLevelColor(level)}>
                    {count}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl size="medium" sx={{ width: '200px' }}>
              <InputLabel>{t('admin.logViewer.level')}</InputLabel>
              <Select
                value={levelFilter}
                onChange={handleLevelChange}
                label={t('admin.logViewer.level')}
                sx={{ width: '200px' }}
                multiple
                renderValue={(selected) => selected.join(', ')}
              >
                {levels.map(level => (
                  <MenuItem key={level} value={level}>
                    <Checkbox checked={levelFilter.indexOf(level) > -1} />
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl size="medium" sx={{ width: '350px' }}>
              <InputLabel>{t('admin.logViewer.process')}</InputLabel>
              <Select
                value={processFilter}
                onChange={handleProcessChange}
                label={t('admin.logViewer.process')}
                sx={{ width: '350px' }}
                multiple
                renderValue={(selected) => selected.join(', ')}
              >
                {processes.map(process => (
                  <MenuItem key={process} value={process}>
                    <Checkbox checked={processFilter.indexOf(process) > -1} />
                    {process}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocales[i18n.language]}>
              <DateTimePicker
                label={t('admin.logViewer.startDate')}
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocales[i18n.language]}>
              <DateTimePicker
                label={t('admin.logViewer.endDate')}
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              className="button"
            >
              {t('admin.logViewer.clearFilters')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Actions */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadLogs}
          disabled={loading}
          className="button"
        >
          {t('admin.logViewer.refresh')}
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={exportLogs}
          disabled={logs.length === 0}
          className="button"
        >
          {t('admin.logViewer.export')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<DeleteIcon />}
          onClick={openDeleteDialog}
          className="button"
        >
          {t('admin.logViewer.cleanup')}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Delete Logs Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.logViewer.deleteLogsDialog.title')}</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
            <FormLabel component="legend">{t('admin.logViewer.deleteLogsDialog.title')}</FormLabel>
            <RadioGroup value={deleteOption} onChange={handleDeleteOptionChange}>
              <FormControlLabel
                value="old"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {t('admin.logViewer.deleteLogsDialog.oldLogsOption')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.logViewer.deleteLogsDialog.oldLogsDescription')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium" color="error">
                      {t('admin.logViewer.deleteLogsDialog.allLogsOption')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('admin.logViewer.deleteLogsDialog.allLogsDescription')}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {showPasswordField && (
            <TextField
              fullWidth
              label={t('admin.logViewer.deleteLogsDialog.passwordRequired')}
              placeholder={t('admin.logViewer.deleteLogsDialog.passwordPlaceholder')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={handlePasswordChange}
              error={!!passwordError}
              helperText={passwordError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          )}

          <DialogContentText>
            {deleteOption === 'old' 
              ? t('admin.logViewer.deleteLogsDialog.confirmOldLogs')
              : t('admin.logViewer.deleteLogsDialog.confirmAllLogs')
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            {t('admin.logViewer.deleteLogsDialog.cancel')}
          </Button>
          <Button 
            onClick={deleteLogs} 
            color="error" 
            variant="contained"
            disabled={deleteOption === 'all' && !password}
          >
            {deleteOption === 'old' 
              ? t('admin.logViewer.deleteLogsDialog.deleteOldLogs')
              : t('admin.logViewer.deleteLogsDialog.deleteAllLogs')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.logViewer.timestamp')}</TableCell>
                <TableCell>{t('admin.logViewer.process')}</TableCell>
                <TableCell>{t('admin.logViewer.level')}</TableCell>
                <TableCell>{t('admin.logViewer.message')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {t('admin.logViewer.noLogs')}
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                    <TableCell>{log.process}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getLevelIcon(log.level)}
                        label={log.level}
                        color={getLevelColor(log.level)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 500, wordBreak: 'break-word' }}>
                      <Box>
                        <Typography variant="body2">
                          {log.message}
                        </Typography>
                        {log.additional_data && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mt: 1, 
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              backgroundColor: 'grey.100',
                              p: 1,
                              borderRadius: 1,
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {JSON.stringify(log.additional_data, null, 2)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.of')} ${count}`}
        />
      </Paper>
    </Box>
  );
};

export default LogViewer; 